import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useApi, useMutation } from '../../lib/useApi.js';
import { api } from '../../lib/apiClient.js';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import { usePageMetadata } from '../../app/PageMetaContext.jsx';
import {
  Breadcrumbs,
  Drawer,
  DrawerForm,
  DrawerFormActions,
  EmptyState,
  ErrorState,
  FormField,
  LoadingSkeleton,
  PageActionStrip,
  PageHeader,
  StatusChip,
  TextInput,
  Textarea,
  DateInput,
  PageViewerControls,
  toast,
  KpiHeroBand,
  deriveKpiTone,
  KpiBandSkeleton,
  StatusDonutMini,
  InlineScoreCell,
  BrandedEmptyState,
  StaggerReveal,
} from '../../components/index.js';
import { PageHeaderDefaultActionsContext, Select } from '../../components/primitives.jsx';
import { useClientPagination } from '../../lib/usePagination.js';
import { usePeople } from '../../lib/usePeople.js';
import { PersonLabel } from '../../components/primitives/PersonLabel.jsx';
import { PersonPicker } from '../../components/primitives/PersonPicker.jsx';
import { MentionCommentComposer } from './MentionCommentComposer.jsx';
import { TimeEntryDrawer } from '../time/TimeEntryDrawer.jsx';
import './actions.css';
import './actions.redesign.css';

function normalizeDateOnly(value) {
  if (!value) return null;
  return String(value).slice(0, 10);
}

function isClosed(status) {
  return /complete|completed|closed|done/i.test(String(status || ''));
}

function isOverdue(action, nowIso = new Date().toISOString()) {
  if (!action?.dueDate || isClosed(action.status)) return false;
  return normalizeDateOnly(action.dueDate) < normalizeDateOnly(nowIso);
}

// Whole days from `now` until the action's due date. Negative => overdue.
function daysUntilDue(action, nowIso = new Date().toISOString()) {
  const due = normalizeDateOnly(action?.dueDate);
  if (!due) return null;
  const dueMs = Date.parse(`${due}T00:00:00Z`);
  const nowMs = Date.parse(`${normalizeDateOnly(nowIso)}T00:00:00Z`);
  if (Number.isNaN(dueMs) || Number.isNaN(nowMs)) return null;
  return Math.round((dueMs - nowMs) / 86400000);
}

// Executive-traffic-light tone for a due date relative to today.
// Closed actions read neutral; overdue -> brick; due soon -> amber; etc.
function dueAgingTone(action, nowIso = new Date().toISOString()) {
  if (isClosed(action?.status)) return 'sage';
  const days = daysUntilDue(action, nowIso);
  if (days == null) return 'slate';
  if (days < 0) return 'brick';
  if (days <= 3) return 'amber';
  if (days <= 14) return 'blue';
  return 'sage';
}

function dueAgingLabel(action, nowIso = new Date().toISOString()) {
  if (isClosed(action?.status)) return 'Closed';
  const days = daysUntilDue(action, nowIso);
  if (days == null) return 'No due date';
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return 'Due today';
  if (days === 1) return 'Due tomorrow';
  return `Due in ${days}d`;
}

// Map an aging tone to a 0-100 "urgency runway" score for InlineScoreCell.
// Higher score = healthier runway. Overdue floors near zero.
function dueRunwayScore(action, nowIso = new Date().toISOString()) {
  if (isClosed(action?.status)) return 100;
  const days = daysUntilDue(action, nowIso);
  if (days == null) return 50;
  if (days < 0) return 4;
  return Math.max(8, Math.min(100, Math.round((days / 30) * 100)));
}

const PRIORITY_TONE = {
  critical: 'brick',
  high: 'brick',
  medium: 'amber',
  low: 'blue',
};

function priorityTone(priority) {
  return PRIORITY_TONE[String(priority || 'medium').toLowerCase()] || 'slate';
}

// Count-based tone where zero is the goal (lower is better).
function deriveKpiToneSafe(count) {
  return deriveKpiTone({ value: count, target: 0, invert: true, atRisk: count > 0 });
}

function statusTone(status) {
  const value = String(status || '').toLowerCase();
  if (isClosed(value)) return 'sage';
  if (/block|denied|reject|hold/.test(value)) return 'brick';
  if (/review|pending|wait/.test(value)) return 'amber';
  if (/progress|active|open/.test(value)) return 'blue';
  return 'slate';
}

function decapitalize(text) {
  const value = String(text || '').trim();
  if (!value) return 'risk follow-up';
  return value.charAt(0).toLowerCase() + value.slice(1);
}

function groupActionsForUser(actions, userId) {
  return {
    assigned: actions.filter(
      (action) =>
        !isClosed(action.status) &&
        (action.ownerUserId === userId || action.assignedToUserId === userId),
    ),
    overdue: actions.filter((action) => isOverdue(action)),
    watching: actions.filter(
      (action) => !isClosed(action.status) && (action.watcherUserIds || []).includes(userId),
    ),
    mentioned: actions.filter(
      (action) => !isClosed(action.status) && (action.mentionedUserIds || []).includes(userId),
    ),
    recentlyCompleted: actions.filter((action) => isClosed(action.status)),
  };
}

function actionSourceLabel(action) {
  if (action?.sourceEntityType && action?.sourceEntityId) {
    return `${action.sourceEntityType}:${action.sourceEntityId}`;
  }
  if (action?.projectId) return `project:${action.projectId}`;
  return 'manual';
}

function actionSourceToken(action) {
  return String(action?.sourceEntityType || 'manual').toLowerCase() || 'manual';
}

// Mirrors the server's evidence routing (buildEvidenceRoute) so a source chip on a
// card/detail navigates back to the originating record (AC-ACT-2.2 / W-2 / W-4 / W-8).
function actionSourceRoute(action) {
  const token = String(action?.sourceEntityType || '').toLowerCase();
  const id = action?.sourceEntityId;
  if (!token || !id) return null;
  if (token === 'risk') {
    return action?.projectId ? `/projects/${action.projectId}?tab=risks` : '/governance/risks';
  }
  if (token === 'meeting') return `/meetings/${id}`;
  if (token === 'feedback') return `/feedback/${action.sourceFeedbackPackageId || id}`;
  if (token === 'project') return `/projects/${id}?tab=actions`;
  if (token === 'artifact') return `/knowledge/artifacts/${id}`;
  return null;
}

// ----- URL-held bucket filters (AC-ACT-1.1 / BL-02) ------------------------
const ACTION_FILTER_KEYS = ['status', 'priority', 'source', 'assignee', 'bucket'];
const ACTION_PRIORITY_ORDER = ['critical', 'high', 'medium', 'low'];

// Posture-tile buckets: each value mirrors exactly one Accountability Posture
// KPI definition so a tile's number and its landed queue always agree.
const ACTION_BUCKET_LABELS = {
  open: 'Open actions',
  overdue: 'Overdue now',
  'high-critical': 'High & critical',
  'completed-recent': 'Completed lately',
};

function readActionFilters(searchParams) {
  const filters = {};
  for (const key of ACTION_FILTER_KEYS) {
    const value = searchParams.get(key);
    if (value) filters[key] = key === 'assignee' ? value : value.toLowerCase();
  }
  return filters;
}

function isAssignedOpen(action, userId) {
  return (
    !isClosed(action?.status) &&
    (action?.ownerUserId === userId || action?.assignedToUserId === userId)
  );
}

function actionMatchesBucket(action, bucket, userId) {
  if (bucket === 'open') return isAssignedOpen(action, userId);
  if (bucket === 'overdue') return isOverdue(action);
  if (bucket === 'high-critical') {
    return isAssignedOpen(action, userId) && /high|critical/i.test(String(action?.priority || ''));
  }
  if (bucket === 'completed-recent') {
    return isClosed(action?.status) && isWithinCompletedWindow(action);
  }
  return true; // unknown bucket values never hide work
}

function actionMatchesFilters(action, filters, userId) {
  if (filters.status && String(action?.status || '').toLowerCase() !== filters.status) return false;
  if (filters.priority && String(action?.priority || 'medium').toLowerCase() !== filters.priority) {
    return false;
  }
  if (filters.source && actionSourceToken(action) !== filters.source) return false;
  if (
    filters.assignee &&
    action?.ownerUserId !== filters.assignee &&
    action?.assignedToUserId !== filters.assignee
  ) {
    return false;
  }
  if (filters.bucket && !actionMatchesBucket(action, filters.bucket, userId)) return false;
  return true;
}

// ----- "Recently completed" window (AC-ACT-1.1) ----------------------------
const COMPLETED_WINDOW_DAYS = 14;

function completedAtOf(action) {
  return (
    action?.closedAt || action?.completedAt || action?.lastUpdatedAt || action?.updatedAt || null
  );
}

function isWithinCompletedWindow(action, nowIso = new Date().toISOString()) {
  const timestamp = completedAtOf(action);
  if (!timestamp) return true; // no closure timestamp — keep visible rather than silently hiding
  const ageMs = Date.parse(nowIso) - Date.parse(timestamp);
  if (Number.isNaN(ageMs)) return true;
  return ageMs <= COMPLETED_WINDOW_DAYS * 86400000;
}

function ActionFilterChips({ actions, filters, onToggle, onClear }) {
  const { nameOf } = usePeople();
  const groups = useMemo(() => {
    const distinct = (mapFn) => [...new Set(actions.map(mapFn).filter(Boolean))];
    return [
      {
        key: 'status',
        label: 'Status',
        values: distinct((action) => String(action.status || '').toLowerCase()).sort(),
        labelOf: (value) => value.replace(/_/g, ' '),
      },
      {
        key: 'priority',
        label: 'Priority',
        values: ACTION_PRIORITY_ORDER.filter((priority) =>
          actions.some((action) => String(action.priority || 'medium').toLowerCase() === priority),
        ),
        labelOf: (value) => value,
      },
      {
        key: 'source',
        label: 'Source',
        values: distinct(actionSourceToken).sort(),
        labelOf: (value) => value,
      },
      {
        key: 'assignee',
        label: 'Assignee',
        values: distinct((action) => action.ownerUserId || action.assignedToUserId).sort(),
        labelOf: (value) => nameOf(value),
      },
      {
        key: 'bucket',
        label: 'View',
        values: Object.keys(ACTION_BUCKET_LABELS),
        labelOf: (value) => ACTION_BUCKET_LABELS[value],
      },
    ];
  }, [actions, nameOf]);

  const hasActive = ACTION_FILTER_KEYS.some((key) => filters[key]);

  return (
    <div className="action-filterbar" data-testid="action-filters">
      {groups.map((group) =>
        group.values.length ? (
          <div
            className="action-filter-group"
            key={group.key}
            role="group"
            aria-label={`Filter by ${group.label.toLowerCase()}`}
          >
            <span className="action-filter-group__label">{group.label}</span>
            {group.values.map((value) => {
              const active = filters[group.key] === value;
              return (
                <button
                  key={value}
                  type="button"
                  className={`chip sm action-filter-chip${active ? ' is-active' : ' outline'}`}
                  aria-pressed={active}
                  data-testid={`action-filter-${group.key}-${value}`}
                  onClick={() => onToggle(group.key, value)}
                >
                  {group.labelOf(value)}
                </button>
              );
            })}
          </div>
        ) : null,
      )}
      {hasActive ? (
        <button
          type="button"
          className="btn ghost sm"
          data-testid="action-filters-clear"
          onClick={onClear}
        >
          Clear filters
        </button>
      ) : null}
    </div>
  );
}

function formatAuditActionLabel(value) {
  const token = String(value || 'audit.recorded').trim();
  if (!token) return 'Audit recorded';
  return token
    .split(/[._]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

// IMP-001 (display half): comment bodies persist raw `@USR-…` mention tokens
// because the server routes governed notifications on the literal user id. For
// DISPLAY we resolve each token to the mentioned person's display name (keeping
// the raw id in a title tooltip for disclosure, mirroring PersonLabel), and fall
// back to the raw id when it cannot be resolved so data is never hidden. A
// `@`-triggered person picker (write-side autocomplete) is intentionally
// deferred by coordinator decision — this fix only resolves the rendered token.
const COMMENT_MENTION_SPLIT = /(@USR-[A-Za-z0-9_-]+)/g;
const COMMENT_MENTION_MATCH = /^@(USR-[A-Za-z0-9_-]+)$/;

function renderCommentBody(body, nameOf) {
  const text = String(body || '');
  if (!text) return null;
  return text.split(COMMENT_MENTION_SPLIT).map((segment, index) => {
    const match = COMMENT_MENTION_MATCH.exec(segment);
    if (!match) return segment;
    const userId = match[1];
    return (
      <span
        key={`mention-${index}-${userId}`}
        className="comment-mention"
        data-testid={`comment-mention-${userId}`}
        title={userId}
      >
        @{nameOf(userId)}
      </span>
    );
  });
}

function ActionListCard({ action }) {
  const aging = dueAgingTone(action);
  const priority = action.priority || 'medium';
  const hasDue = Boolean(normalizeDateOnly(action.dueDate));
  const sourceRoute = actionSourceRoute(action);
  return (
    <article className={`action-card aging-${aging}`}>
      <div className="action-card__lead">
        <div>
          <h3 className="action-card__title">
            <Link to={`/actions/${action.id}`}>{action.title || action.id}</Link>
          </h3>
          <p className="action-card__body">{action.description || 'No action detail provided.'}</p>
        </div>
        <div className="action-card__chips">
          <StatusChip status={action.status} />
          <span className={`chip outline sm action-priority-chip tone-${priorityTone(priority)}`}>
            {priority}
          </span>
        </div>
      </div>
      <div className="action-card__duebar">
        <span className={`action-aging-chip tone-${aging}`}>
          <span className="chip-dot" aria-hidden />
          {dueAgingLabel(action)}
        </span>
        {hasDue && !isClosed(action.status) ? (
          <InlineScoreCell
            value={dueRunwayScore(action)}
            max={100}
            tone={aging}
            suffix=""
            ariaLabel={`Due runway: ${dueAgingLabel(action)}`}
          />
        ) : null}
      </div>
      <div className="action-card__meta">
        <span>{action.id}</span>
        <span>
          <PersonLabel userId={action.ownerUserId || action.assignedToUserId} />
        </span>
        <span>{action.dueDate || 'no due date'}</span>
        {sourceRoute ? (
          <Link
            className="action-source-link"
            to={sourceRoute}
            data-testid={`action-source-${action.id}`}
            title={`Open source ${actionSourceToken(action)} record`}
          >
            {actionSourceLabel(action)}
          </Link>
        ) : (
          <span>{actionSourceLabel(action)}</span>
        )}
      </div>
    </article>
  );
}

function ActionBucketSection({
  title,
  bucketKey,
  items,
  emptyCopy,
  emptyTone = 'slate',
  spanClass = '',
  headerAside = null,
}) {
  const pager = useClientPagination(items, {
    initialPageSize: 4,
    resetKey: `${bucketKey}-${items.length}`,
  });
  // Tone the count chip by how loud this bucket is for senior leadership.
  const countTone = bucketKey === 'overdue' && items.length > 0 ? 'brick' : 'outline';
  return (
    <section
      className={`action-bucket card ${spanClass}`}
      data-testid={`action-bucket-${bucketKey}`}
    >
      <div className="row spread action-bucket__header">
        <h2 className="action-bucket__title">{title}</h2>
        <div className="row action-bucket__header-aside" style={{ gap: 8 }}>
          {headerAside}
          <span className={`chip ${countTone} sm action-bucket__count`}>{items.length}</span>
        </div>
      </div>
      {items.length === 0 ? (
        <BrandedEmptyState
          tone={emptyTone}
          eyebrow="Action queue"
          title="Nothing here"
          description={emptyCopy}
        />
      ) : (
        <>
          <StaggerReveal as="div" className="action-bucket__stack">
            {pager.items.map((action) => (
              <ActionListCard key={action.id} action={action} />
            ))}
          </StaggerReveal>
          <PageViewerControls
            page={pager.page}
            label={title.toLowerCase()}
            pageSize={pager.pageSize}
            onOffsetChange={pager.setOffset}
            onPageSizeChange={pager.setPageSize}
          />
        </>
      )}
    </section>
  );
}

export function ActionCenterPage() {
  const { user } = useCurrentUser();
  const r = useApi('/actions', { query: { limit: 200 } });
  const allActions = useMemo(() => r.data?.data || [], [r.data]);

  // AC-ACT-1.1: header chips are live, URL-held filters (status / priority /
  // source / assignee) so a filtered view survives refresh and deep links.
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => readActionFilters(searchParams), [searchParams]);
  const filteredActions = useMemo(
    () => allActions.filter((action) => actionMatchesFilters(action, filters, user?.id)),
    [allActions, filters, user?.id],
  );

  function toggleFilter(key, value) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (next.get(key) === value) next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace: true },
    );
  }

  function clearFilters() {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const key of ACTION_FILTER_KEYS) next.delete(key);
        return next;
      },
      { replace: true },
    );
  }

  const buckets = useMemo(
    () => groupActionsForUser(filteredActions, user?.id),
    [filteredActions, user?.id],
  );

  // AC-ACT-1.1: "Recently completed" is truly windowed (last 14 days, labeled),
  // with an explicit disclosure to view the full completed history.
  const completedAll = useMemo(
    () =>
      [...buckets.recentlyCompleted].sort((left, right) =>
        String(completedAtOf(right) || '').localeCompare(String(completedAtOf(left) || '')),
      ),
    [buckets.recentlyCompleted],
  );
  const completedRecent = useMemo(
    () => completedAll.filter((action) => isWithinCompletedWindow(action)),
    [completedAll],
  );
  const [showAllCompleted, setShowAllCompleted] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  // Open = anything assigned-but-not-closed in the user's accountable set.
  // Reuse the already-grouped buckets so nothing is re-fetched or fabricated.
  const rollups = useMemo(() => {
    const open = buckets.assigned;
    const high = open.filter((a) => /high|critical/i.test(String(a.priority || ''))).length;
    const dueSoon = open.filter((a) => {
      const days = daysUntilDue(a);
      return days != null && days >= 0 && days <= 3;
    }).length;

    const statusCounts = open.reduce((acc, a) => {
      const tone = statusTone(a.status);
      acc[tone] = (acc[tone] || 0) + 1;
      return acc;
    }, {});
    const statusSegments = [
      { label: 'In progress', tone: 'blue', count: statusCounts.blue || 0 },
      { label: 'In review', tone: 'amber', count: statusCounts.amber || 0 },
      { label: 'Blocked', tone: 'brick', count: statusCounts.brick || 0 },
      { label: 'Other', tone: 'slate', count: statusCounts.slate || 0 },
    ];

    const prioCounts = open.reduce((acc, a) => {
      const tone = priorityTone(a.priority);
      acc[tone] = (acc[tone] || 0) + 1;
      return acc;
    }, {});
    const prioritySegments = [
      { label: 'High / critical', tone: 'brick', count: prioCounts.brick || 0 },
      { label: 'Medium', tone: 'amber', count: prioCounts.amber || 0 },
      { label: 'Low', tone: 'blue', count: prioCounts.blue || 0 },
    ];

    return {
      openCount: open.length,
      overdueCount: buckets.overdue.length,
      highCount: high,
      dueSoonCount: dueSoon,
      completedCount: completedRecent.length,
      statusSegments,
      prioritySegments,
    };
  }, [buckets, completedRecent]);

  const kpiItems = useMemo(
    () => [
      {
        id: 'open',
        label: 'Open actions',
        value: rollups.openCount,
        unit: rollups.openCount === 1 ? 'action' : 'actions',
        status: 'blue',
        footer: `${rollups.dueSoonCount} due within 3 days`,
        to: '/actions?bucket=open',
      },
      {
        id: 'overdue',
        label: 'Overdue now',
        value: rollups.overdueCount,
        unit: rollups.overdueCount === 1 ? 'action' : 'actions',
        status: deriveKpiToneSafe(rollups.overdueCount),
        statusLabel: rollups.overdueCount > 0 ? 'Attention' : 'Clear',
        footer: 'Past due, not yet closed',
        to: '/actions?bucket=overdue',
      },
      {
        id: 'high-priority',
        label: 'High / critical',
        value: rollups.highCount,
        unit: rollups.highCount === 1 ? 'action' : 'actions',
        status: deriveKpiToneSafe(rollups.highCount),
        statusLabel: rollups.highCount > 0 ? 'Watch' : 'Stable',
        footer: 'Highest-priority assigned work',
        to: '/actions?bucket=high-critical',
      },
      {
        id: 'completed',
        label: 'Completed lately',
        value: rollups.completedCount,
        unit: rollups.completedCount === 1 ? 'action' : 'actions',
        status: 'sage',
        footer: `Closed in the last ${COMPLETED_WINDOW_DAYS} days`,
        to: '/actions?bucket=completed-recent',
      },
    ],
    [rollups],
  );

  usePageMetadata({
    title: 'My Action Center',
    subtitle:
      'Assigned, overdue, watched, and mentioned actions tied to OWHA delivery accountability.',
  });

  if (r.loading) {
    return (
      <>
        <PageHeader eyebrow="Collaboration" title="My Action Center" />
        <KpiBandSkeleton count={4} />
        <LoadingSkeleton rows={6} />
      </>
    );
  }
  if (r.error) return <ErrorState error={r.error} onRetry={r.refresh} />;

  const hasOpen = rollups.openCount > 0;

  return (
    <div className="actions-redesign" data-testid="action-center-page">
      <PageHeader
        eyebrow="Collaboration"
        title="My Action Center"
        subtitle="Track assigned work, overdue follow-through, watched items, and @mentions across the OWHA lifecycle."
      />
      <PageActionStrip
        filters={
          <ActionFilterChips
            actions={allActions}
            filters={filters}
            onToggle={toggleFilter}
            onClear={clearFilters}
          />
        }
        actions={
          <button type="button" className="btn primary" onClick={() => setCreateOpen(true)}>
            New action
          </button>
        }
      />
      <p className="muted small action-source-hint">
        Actions also arrive from risks, meetings, and AI reviews — source chips link back to the
        originating record.
      </p>

      <KpiHeroBand
        eyebrow="Accountability posture"
        asOf={r.loadedAt}
        items={kpiItems}
        columns={4}
      />

      {hasOpen ? (
        <div className="action-rollups">
          <section className="card action-rollup-card" data-testid="action-status-rollup">
            <StatusDonutMini
              segments={rollups.statusSegments}
              size={104}
              totalLabel="Open"
              ariaLabel="Open actions by status"
            />
            <div className="action-rollup-card__body">
              <span className="eyebrow">By status</span>
              <p className="muted small action-rollup-card__hint">
                Live workflow state across your open accountable actions.
              </p>
            </div>
          </section>
          <section className="card action-rollup-card" data-testid="action-priority-rollup">
            <StatusDonutMini
              segments={rollups.prioritySegments}
              size={104}
              totalLabel="Open"
              ariaLabel="Open actions by priority"
            />
            <div className="action-rollup-card__body">
              <span className="eyebrow">By priority</span>
              <p className="muted small action-rollup-card__hint">
                Where the highest-priority follow-through is concentrated.
              </p>
            </div>
          </section>
        </div>
      ) : null}

      <div className="action-grid action-bento">
        <ActionBucketSection
          title="Assigned to me"
          bucketKey="assigned"
          items={buckets.assigned}
          emptyCopy="No open actions are assigned to you."
          emptyTone="sage"
        />
        <ActionBucketSection
          title="Overdue"
          bucketKey="overdue"
          items={buckets.overdue}
          emptyCopy="No overdue actions are currently tracked."
          emptyTone="sage"
        />
        <ActionBucketSection
          title="Watching"
          bucketKey="watching"
          items={buckets.watching}
          emptyCopy="You are not watching any open actions."
        />
        <ActionBucketSection
          title="Mentioned"
          bucketKey="mentioned"
          items={buckets.mentioned}
          emptyCopy="No current @mentions require follow-up."
        />
        <ActionBucketSection
          title={
            showAllCompleted
              ? 'Completed (all history)'
              : `Recently completed · last ${COMPLETED_WINDOW_DAYS} days`
          }
          bucketKey="recently-completed"
          items={showAllCompleted ? completedAll : completedRecent}
          emptyCopy={`No actions were completed in the last ${COMPLETED_WINDOW_DAYS} days.`}
          headerAside={
            completedAll.length > completedRecent.length ? (
              <button
                type="button"
                className="btn ghost sm"
                data-testid="completed-window-toggle"
                onClick={() => setShowAllCompleted((current) => !current)}
              >
                {showAllCompleted
                  ? `Show last ${COMPLETED_WINDOW_DAYS} days`
                  : `View all ${completedAll.length}`}
              </button>
            ) : null
          }
        />
      </div>

      {createOpen ? (
        <ActionEditorDrawer open onClose={() => setCreateOpen(false)} onSaved={() => r.refresh()} />
      ) : null}
    </div>
  );
}

export function ActionDetailPage() {
  const { id } = useParams();
  const { nameOf } = usePeople();
  // Bespoke header page: the record h1 lives in the summary card, so the
  // shell's default header action (About this page) is consumed directly
  // instead of via a PageHeader component (PRIME-SHELL-UX-001).
  const defaultHeaderActions = useContext(PageHeaderDefaultActionsContext);
  const r = useApi(`/actions/${id}`);
  const detail = r.data?.data;
  const action = detail?.action;
  const [timeDrawerOpen, setTimeDrawerOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const commentMut = useMutation();
  const handoffMut = useMutation();
  const completeMut = useMutation();
  const [commentDraft, setCommentDraft] = useState('');
  const [commentMentionedUserIds, setCommentMentionedUserIds] = useState([]);
  const [handoffOwner, setHandoffOwner] = useState('');
  const [handoffReason, setHandoffReason] = useState('');
  const [completionNote, setCompletionNote] = useState('');

  useEffect(() => {
    setCompletionNote(action?.completionNote || '');
  }, [action?.id, action?.completionNote]);

  async function submitComment() {
    const body = commentDraft.trim();
    if (!action?.id || !body) return;
    try {
      await commentMut.run(() => {
        const payload = {
          scopeType: 'action',
          scopeId: action.id,
          body,
        };
        if (commentMentionedUserIds.length) {
          payload.mentionedUserIds = commentMentionedUserIds;
        }
        return api.post('/comments', payload);
      });
      setCommentDraft('');
      setCommentMentionedUserIds([]);
      toast.success({
        title: 'Comment posted',
        body: 'Action collaboration notes were updated.',
      });
      await r.refresh();
    } catch (error) {
      toast.error({
        title: 'Could not post comment',
        body: error.message,
      });
    }
  }

  async function submitHandoff() {
    const toUserId = handoffOwner.trim();
    const reason = handoffReason.trim();
    if (!action?.id || !toUserId || !reason) return;
    try {
      await handoffMut.run(() =>
        api.post(`/actions/${action.id}/handoff`, {
          toUserId,
          reason,
        }),
      );
      setHandoffOwner('');
      setHandoffReason('');
      toast.success({
        title: 'Action handed off',
        body: `Ownership moved to ${nameOf(toUserId)}.`,
      });
      await r.refresh();
    } catch (error) {
      toast.error({
        title: 'Could not hand off action',
        body: error.message,
      });
    }
  }

  async function submitCompletion() {
    const note = completionNote.trim();
    if (!action?.id || !note || isClosed(action.status)) return;
    try {
      await completeMut.run(() =>
        api.post(`/actions/${action.id}/complete`, {
          completionNote: note,
        }),
      );
      toast.success({
        title: 'Action completed',
        body: action.title,
      });
      await r.refresh();
    } catch (error) {
      toast.error({
        title: 'Could not complete action',
        body: error.message,
      });
    }
  }

  usePageMetadata({
    title: action?.title || 'Action detail',
    subtitle: action?.description || 'Comments, handoffs, and audit-facing execution context.',
  });

  if (r.loading) return <LoadingSkeleton rows={6} />;
  // AC-ACT-2.4: a missing action offers an explicit route back to the center.
  const notFoundState = (
    <div className="actions-redesign">
      <EmptyState
        title="Action not found"
        description="The requested action does not exist or is no longer available."
      />
      <Link className="btn secondary" to="/actions" data-testid="action-404-back">
        Back to Action Center
      </Link>
    </div>
  );
  if (r.error?.status === 404) return notFoundState;
  if (r.error) return <ErrorState error={r.error} onRetry={r.refresh} />;
  if (!action) return notFoundState;

  const detailAging = dueAgingTone(action);
  const detailStatusSegments = [{ label: 'Status', tone: statusTone(action.status), count: 1 }];
  const detailPrioritySegments = [
    { label: 'Priority', tone: priorityTone(action.priority), count: 1 },
  ];
  const hasDetailDue = Boolean(normalizeDateOnly(action.dueDate));

  return (
    <div className="actions-redesign">
      <Breadcrumbs
        items={[{ to: '/actions', label: 'My Action Center' }, { label: action.title }]}
      />
      <PageActionStrip
        filters={
          <>
            <StatusChip status={action.status} />
            <span
              className={`chip outline sm action-priority-chip tone-${priorityTone(action.priority)}`}
            >
              {action.priority || 'medium'}
            </span>
            <span className="chip outline sm">
              <PersonLabel userId={action.ownerUserId} />
            </span>
            <span className={`action-aging-chip tone-${detailAging}`}>
              <span className="chip-dot" aria-hidden />
              {action.dueDate ? `${action.dueDate} · ${dueAgingLabel(action)}` : 'No due date'}
            </span>
          </>
        }
        actions={
          <>
            <button type="button" className="btn secondary" onClick={() => setEditOpen(true)}>
              Edit action
            </button>
            <button type="button" className="btn secondary" onClick={() => setTimeDrawerOpen(true)}>
              Log time
            </button>
          </>
        }
      />

      <div className="action-detail-rollups">
        <section className="card action-posture-card" data-testid="action-detail-status">
          <StatusDonutMini
            segments={detailStatusSegments}
            size={92}
            totalLabel="State"
            centerValue=" "
            ariaLabel="Action workflow status"
          />
          <div className="action-posture-card__body">
            <span className="eyebrow">Workflow status</span>
            <strong>{action.status || 'open'}</strong>
            <p className="muted small">
              <PersonLabel userId={action.ownerUserId} />
            </p>
          </div>
        </section>
        <section className="card action-posture-card" data-testid="action-detail-priority">
          <StatusDonutMini
            segments={detailPrioritySegments}
            size={92}
            totalLabel="Level"
            centerValue=" "
            ariaLabel="Action priority"
          />
          <div className="action-posture-card__body">
            <span className="eyebrow">Priority</span>
            <strong>{action.priority || 'medium'}</strong>
            <p className="muted small">{actionSourceLabel(action)}</p>
          </div>
        </section>
        <section className="card action-posture-card" data-testid="action-detail-due">
          <div className="action-posture-card__body" style={{ flex: 1 }}>
            <span className="eyebrow">Due runway</span>
            <strong>{dueAgingLabel(action)}</strong>
            {hasDetailDue && !isClosed(action.status) ? (
              <div className="action-posture-due">
                <InlineScoreCell
                  value={dueRunwayScore(action)}
                  max={100}
                  tone={detailAging}
                  ariaLabel={`Due runway: ${dueAgingLabel(action)}`}
                />
              </div>
            ) : (
              <p className="muted small">{action.dueDate || 'No due date set'}</p>
            )}
          </div>
        </section>
      </div>

      <div className="action-detail-layout">
        <section className="card action-detail-card">
          <div className="action-detail-title-row">
            <div>
              <div className="eyebrow">Action summary</div>
              <h1 className="action-detail-title">{action.title}</h1>
            </div>
            {defaultHeaderActions}
          </div>
          <p className="action-detail-body">{action.description || 'No description provided.'}</p>
          <div className="action-card__meta">
            <span>{action.id}</span>
            <span>{action.projectId || 'cross-workspace'}</span>
            {actionSourceRoute(action) ? (
              <Link
                className="action-source-link"
                to={actionSourceRoute(action)}
                data-testid="action-detail-source-link"
                title={`Open source ${actionSourceToken(action)} record`}
              >
                {action.sourceEntityType}:{action.sourceEntityId}
              </Link>
            ) : (
              <span>
                {action.sourceEntityType || 'manual'}:{action.sourceEntityId || 'n/a'}
              </span>
            )}
          </div>
        </section>

        <section className="card action-detail-card">
          <div className="eyebrow">Workflow controls</div>
          <div className="action-control-stack">
            <div className="action-control-block">
              <FormField label="Add comment" htmlFor="action-comment-input">
                <MentionCommentComposer
                  id="action-comment-input"
                  rows={3}
                  value={commentDraft}
                  onChange={setCommentDraft}
                  mentionedUserIds={commentMentionedUserIds}
                  onMentionedUserIdsChange={setCommentMentionedUserIds}
                  placeholder="Add collaboration notes and @mention the next reviewer."
                />
              </FormField>
              <div className="action-control-footer">
                <span className="muted small">Type @ to search people by name or email.</span>
                <button
                  type="button"
                  className="btn secondary sm"
                  disabled={commentMut.running || !commentDraft.trim()}
                  onClick={submitComment}
                >
                  Post comment
                </button>
              </div>
            </div>

            <div className="action-control-block">
              <div className="action-control-grid">
                <PersonPicker
                  id="action-handoff-owner"
                  label="Handoff owner"
                  value={handoffOwner}
                  onChange={setHandoffOwner}
                  placeholder="Select the new owner…"
                />
                <FormField label="Handoff reason" htmlFor="action-handoff-reason">
                  <Textarea
                    id="action-handoff-reason"
                    rows={3}
                    value={handoffReason}
                    onChange={setHandoffReason}
                    placeholder="Explain why the new owner should take over."
                  />
                </FormField>
              </div>
              <div className="action-control-footer">
                <span className="muted small">
                  Ownership transfer records audit and alerts the new action owner.
                </span>
                <button
                  type="button"
                  className="btn secondary sm"
                  disabled={
                    handoffMut.running ||
                    isClosed(action.status) ||
                    !handoffOwner.trim() ||
                    !handoffReason.trim()
                  }
                  onClick={submitHandoff}
                >
                  Handoff action
                </button>
              </div>
            </div>

            <div className="action-control-block">
              <FormField label="Completion note" htmlFor="action-completion-note">
                <Textarea
                  id="action-completion-note"
                  rows={3}
                  value={completionNote}
                  onChange={setCompletionNote}
                  placeholder="Summarize what closed the action and any delivered evidence."
                />
              </FormField>
              <div className="action-control-footer">
                <span className="muted small">
                  {isClosed(action.status)
                    ? `Completed ${normalizeDateOnly(action.closedAt) || 'recently'}.`
                    : 'High-priority actions require a completion note before closure.'}
                </span>
                <button
                  type="button"
                  className="btn primary sm"
                  disabled={
                    completeMut.running || isClosed(action.status) || !completionNote.trim()
                  }
                  onClick={submitCompletion}
                >
                  Complete action
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="card action-detail-card">
          <div className="eyebrow">Related evidence</div>
          {detail.relatedEvidence?.entities?.length || detail.relatedEvidence?.artifacts?.length ? (
            <div className="action-evidence-stack">
              {detail.relatedEvidence.entities?.map((item) => (
                <article
                  className="action-evidence-item"
                  key={`${item.entityType}:${item.entityId}`}
                >
                  <div className="row spread" style={{ gap: 12, alignItems: 'flex-start' }}>
                    <div>
                      <div className="action-evidence-label">{item.relationship}</div>
                      {item.route ? (
                        <Link className="action-evidence-title" to={item.route}>
                          {item.title}
                        </Link>
                      ) : (
                        <strong className="action-evidence-title">{item.title}</strong>
                      )}
                    </div>
                    {item.status ? <StatusChip status={item.status} /> : null}
                  </div>
                  {item.summary ? <p>{item.summary}</p> : null}
                </article>
              ))}
              {detail.relatedEvidence.artifacts?.map((artifact) => (
                <article className="action-evidence-item" key={artifact.id}>
                  <div className="row spread" style={{ gap: 12, alignItems: 'flex-start' }}>
                    <div>
                      <div className="action-evidence-label">{artifact.relationship}</div>
                      <Link className="action-evidence-title" to={artifact.route}>
                        {artifact.title}
                      </Link>
                    </div>
                    <div className="action-card__chips">
                      {artifact.status ? <StatusChip status={artifact.status} /> : null}
                      {artifact.classification ? (
                        <span className="chip outline sm">{artifact.classification}</span>
                      ) : null}
                    </div>
                  </div>
                  {artifact.summary ? <p>{artifact.summary}</p> : null}
                </article>
              ))}
            </div>
          ) : (
            <EmptyState
              title="No related evidence"
              description="No linked source records or knowledge artifacts are attached to this action yet."
            />
          )}
        </section>

        <section className="card action-detail-card">
          <div className="eyebrow">Audit posture</div>
          <div className="action-audit-summary">
            <strong>{detail.audit?.summary?.totalEvents || 0} events recorded</strong>
            <div className="action-card__meta">
              <span>{detail.audit?.summary?.latestAction || 'No mutations yet'}</span>
              <span>{detail.audit?.summary?.latestEventAt?.slice(0, 16) || '—'}</span>
            </div>
          </div>
          {detail.audit?.events?.length ? (
            <ul className="action-timeline">
              {detail.audit.events.map((event) => (
                <li key={event.id}>
                  <div className="row spread" style={{ gap: 12 }}>
                    <strong>{formatAuditActionLabel(event.action)}</strong>
                    <span className="muted small">{event.occurredAt?.slice(0, 16) || '—'}</span>
                  </div>
                  <p>{event.summary}</p>
                  <div className="action-card__meta">
                    <span>
                      <PersonLabel userId={event.actorUserId} fallback="system" />
                    </span>
                    <span>{event.result || 'recorded'}</span>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              title="No audit events"
              description="Action-level audit activity will appear here as ownership and status changes occur."
            />
          )}
        </section>

        <section className="card action-detail-card">
          <div className="eyebrow">Comment thread</div>
          {detail.comments?.length ? (
            <ul className="action-timeline">
              {detail.comments.map((comment) => (
                <li key={comment.id}>
                  <div className="row spread" style={{ gap: 12 }}>
                    <strong>
                      <PersonLabel userId={comment.authorUserId} />
                    </strong>
                    <span className="muted small">{comment.createdAt?.slice(0, 16) || '—'}</span>
                  </div>
                  <p>{renderCommentBody(comment.body, nameOf)}</p>
                  {comment.mentionedUserIds?.length ? (
                    <div className="action-card__meta">
                      {comment.mentionedUserIds.map((userId) => (
                        <span key={userId}>
                          @<PersonLabel userId={userId} />
                        </span>
                      ))}
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              title="No comments"
              description="No collaboration notes have been captured yet."
            />
          )}
        </section>

        <section className="card action-detail-card">
          <div className="eyebrow">Handoff history</div>
          {detail.handoffs?.length ? (
            <ul className="action-timeline">
              {detail.handoffs.map((handoff) => (
                <li key={handoff.id}>
                  <div className="row spread" style={{ gap: 12 }}>
                    <strong>
                      <PersonLabel userId={handoff.fromUserId} /> →{' '}
                      <PersonLabel userId={handoff.toUserId} />
                    </strong>
                    <span className="muted small">{handoff.occurredAt?.slice(0, 16) || '—'}</span>
                  </div>
                  <p>{handoff.reason}</p>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              title="No handoffs"
              description="Ownership has remained stable for this action so far."
            />
          )}
        </section>
      </div>

      {editOpen ? (
        <ActionEditorDrawer
          open
          action={action}
          onClose={() => setEditOpen(false)}
          onSaved={() => r.refresh()}
        />
      ) : null}

      <TimeEntryDrawer
        open={timeDrawerOpen}
        onClose={() => setTimeDrawerOpen(false)}
        targetEntityType="action"
        targetEntityId={action.id}
        targetTitle={action.title}
        projectId={action.projectId || null}
        workItemId={null}
        category="action"
      />
    </div>
  );
}

// ----- Create / edit action drawer (AC-ACT-1.2 / AC-ACT-2.3) ----------------
const ACTION_PRIORITY_OPTIONS = ACTION_PRIORITY_ORDER.map((priority) => ({
  value: priority,
  label: priority.charAt(0).toUpperCase() + priority.slice(1),
}));

const ACTION_SOURCE_TYPE_OPTIONS = [
  { value: '', label: 'No linked source' },
  { value: 'risk', label: 'Risk' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'feedback', label: 'Feedback' },
];

function buildEditorDraft(action) {
  return {
    title: action?.title || '',
    ownerUserId: action?.ownerUserId || action?.assignedToUserId || '',
    dueDate: normalizeDateOnly(action?.dueDate) || '',
    priority: String(action?.priority || 'medium').toLowerCase(),
    description: action?.description || '',
    sourceEntityType: action?.sourceEntityType || '',
    sourceEntityId: action?.sourceEntityId || '',
  };
}

export function ActionEditorDrawer({ open, onClose, action = null, onSaved }) {
  const isEdit = Boolean(action?.id);
  const mut = useMutation();
  const navigate = useNavigate();
  const [draft, setDraft] = useState(() => buildEditorDraft(action));
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setDraft(buildEditorDraft(action));
      setErrors({});
    }
  }, [open, action]);

  function update(field, value) {
    setDraft((current) => ({ ...current, [field]: value }));
    if (errors[field]) setErrors((current) => ({ ...current, [field]: null }));
  }

  function validate() {
    const next = {};
    if (!String(draft.title || '').trim()) next.title = 'Action title is required.';
    if (!String(draft.ownerUserId || '').trim()) next.ownerUserId = 'Owner is required.';
    if (!String(draft.dueDate || '').trim()) next.dueDate = 'Due date is required.';
    if (draft.sourceEntityType && !String(draft.sourceEntityId || '').trim()) {
      next.sourceEntityId = 'Provide the source record id (e.g. RSK-001).';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit() {
    if (!validate()) return;
    const ownerUserId = draft.ownerUserId.trim();
    const payload = {
      title: draft.title.trim(),
      description: draft.description.trim(),
      ownerUserId,
      assignedToUserId: ownerUserId,
      dueDate: draft.dueDate || null,
      priority: draft.priority || 'medium',
    };
    try {
      if (isEdit) {
        const response = await mut.run(() => api.patch(`/actions/${action.id}`, payload));
        const updated = response?.data || null;
        toast.success({ title: 'Action updated', body: updated?.title || payload.title });
        onSaved?.(updated);
      } else {
        if (draft.sourceEntityType && draft.sourceEntityId.trim()) {
          payload.sourceEntityType = draft.sourceEntityType;
          payload.sourceEntityId = draft.sourceEntityId.trim();
        }
        const response = await mut.run(() => api.post('/actions', payload));
        const created = response?.data || null;
        toast.success({
          title: 'Action created',
          body: created?.title || payload.title,
          action: created?.id
            ? { label: 'Open action', onClick: () => navigate(`/actions/${created.id}`) }
            : undefined,
        });
        onSaved?.(created);
      }
      onClose?.();
    } catch (error) {
      toast.error({
        title: isEdit ? 'Could not update action' : 'Could not create action',
        body: error.message,
      });
    }
  }

  return (
    <Drawer open={open} onClose={onClose} title={isEdit ? 'Edit action' : 'New action'} width={560}>
      <DrawerForm onSubmit={handleSubmit} submitting={mut.running}>
        <FormField label="Title" htmlFor="action-editor-title" required error={errors.title}>
          <TextInput
            id="action-editor-title"
            value={draft.title}
            onChange={(value) => update('title', value)}
            placeholder="What needs to happen?"
          />
        </FormField>

        <PersonPicker
          id="action-editor-owner"
          label="Owner"
          required
          value={draft.ownerUserId}
          onChange={(value) => update('ownerUserId', value)}
          error={errors.ownerUserId}
        />

        <FormField label="Due date" htmlFor="action-editor-due" required error={errors.dueDate}>
          <DateInput
            id="action-editor-due"
            value={draft.dueDate}
            onChange={(value) => update('dueDate', value)}
          />
        </FormField>

        <FormField label="Priority" htmlFor="action-editor-priority">
          <Select
            id="action-editor-priority"
            value={draft.priority}
            onChange={(value) => update('priority', value)}
            options={ACTION_PRIORITY_OPTIONS}
          />
        </FormField>

        <FormField label="Description" htmlFor="action-editor-description">
          <Textarea
            id="action-editor-description"
            rows={4}
            value={draft.description}
            onChange={(value) => update('description', value)}
            placeholder="Context, evidence expectations, and definition of done."
          />
        </FormField>

        {!isEdit ? (
          <>
            <FormField
              label="Link source (optional)"
              htmlFor="action-editor-source-type"
              hint="Actions created from a risk, meeting, or feedback record link back to it."
            >
              <Select
                id="action-editor-source-type"
                value={draft.sourceEntityType}
                onChange={(value) => update('sourceEntityType', value)}
                options={ACTION_SOURCE_TYPE_OPTIONS}
              />
            </FormField>
            {draft.sourceEntityType ? (
              <FormField
                label="Source record id"
                htmlFor="action-editor-source-id"
                required
                error={errors.sourceEntityId}
              >
                <TextInput
                  id="action-editor-source-id"
                  value={draft.sourceEntityId}
                  onChange={(value) => update('sourceEntityId', value)}
                  placeholder="e.g. RSK-001"
                />
              </FormField>
            ) : null}
          </>
        ) : null}

        <DrawerFormActions
          onCancel={onClose}
          submitLabel={isEdit ? 'Save changes' : 'Create action'}
          submitting={mut.running}
        />
      </DrawerForm>
    </Drawer>
  );
}

function buildDefaultDraft(risk, project) {
  return {
    title: `Mitigate ${decapitalize(risk?.title)}`,
    ownerUserId: project?.managerUserId || risk?.ownerUserId || '',
    dueDate: normalizeDateOnly(risk?.targetMitigationDate) || '',
    description: risk?.mitigationPlan || risk?.description || '',
    note: '',
  };
}

export function RiskToActionDrawer({ open, onClose, risk, project, onCreated }) {
  const mut = useMutation();
  const navigate = useNavigate();
  const [draft, setDraft] = useState(buildDefaultDraft(risk, project));
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setDraft(buildDefaultDraft(risk, project));
      setErrors({});
    }
  }, [open, risk, project]);

  function update(field, value) {
    setDraft((current) => ({ ...current, [field]: value }));
    if (errors[field]) setErrors((current) => ({ ...current, [field]: null }));
  }

  function validate() {
    const next = {};
    if (!String(draft.title || '').trim()) next.title = 'Action title is required.';
    if (!String(draft.ownerUserId || '').trim()) next.ownerUserId = 'Owner is required.';
    if (!String(draft.dueDate || '').trim()) next.dueDate = 'Due date is required.';
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit() {
    if (!risk?.id || !validate()) return;
    try {
      const response = await mut.run(() =>
        api.post(`/risks/${risk.id}/promote-to-action`, {
          title: draft.title.trim(),
          ownerUserId: draft.ownerUserId.trim(),
          dueDate: draft.dueDate,
          description: draft.description.trim(),
          note: draft.note.trim() || undefined,
        }),
      );
      const created = response.data?.action || null;
      toast.success({
        title: 'Action created',
        body: created?.title || risk.title,
        action: created?.id
          ? { label: 'Open action', onClick: () => navigate(`/actions/${created.id}`) }
          : undefined,
      });
      onCreated?.(created);
      onClose?.();
    } catch (error) {
      // AC-GOV-2.3: risk→action is idempotent — on a duplicate attempt the API
      // 409s with the existing action id, which we surface as a deep link.
      const existingActionId = error?.status === 409 ? error?.details?.promotedActionId : null;
      if (existingActionId) {
        toast.error({
          title: 'Action already exists for this risk',
          body: `${risk.id} is already linked to ${existingActionId}.`,
          action: {
            label: 'Open existing action',
            onClick: () => navigate(`/actions/${existingActionId}`),
          },
        });
        onCreated?.(null);
        onClose?.();
        return;
      }
      toast.error({
        title: 'Could not create action',
        body: error.message,
      });
    }
  }

  return (
    <Drawer open={open} onClose={onClose} title="Create action from risk" width={560}>
      <DrawerForm onSubmit={handleSubmit} submitting={mut.running}>
        <div className="action-drawer-note">
          <div className="eyebrow">Source risk</div>
          <strong>{risk?.title || 'No risk selected'}</strong>
          <p>{risk?.description || risk?.mitigationPlan || 'No risk description available.'}</p>
        </div>

        <FormField label="Action title" htmlFor="risk-action-title" required error={errors.title}>
          <TextInput
            id="risk-action-title"
            name="risk-action-title"
            value={draft.title}
            onChange={(value) => update('title', value)}
          />
        </FormField>

        <PersonPicker
          id="risk-action-owner"
          name="risk-action-owner"
          label="Owner"
          required
          value={draft.ownerUserId}
          onChange={(value) => update('ownerUserId', value)}
          error={errors.ownerUserId}
        />

        <FormField label="Due date" htmlFor="risk-action-due" required error={errors.dueDate}>
          <DateInput
            id="risk-action-due"
            name="risk-action-due"
            value={draft.dueDate}
            onChange={(value) => update('dueDate', value)}
          />
        </FormField>

        <FormField label="Mitigation detail" htmlFor="risk-action-description">
          <Textarea
            id="risk-action-description"
            name="risk-action-description"
            rows={4}
            value={draft.description}
            onChange={(value) => update('description', value)}
          />
        </FormField>

        <FormField
          label="Assignment note"
          htmlFor="risk-action-note"
          hint="Optional note shown in the assignment notification."
        >
          <Textarea
            id="risk-action-note"
            name="risk-action-note"
            rows={2}
            value={draft.note}
            onChange={(value) => update('note', value)}
          />
        </FormField>

        <DrawerFormActions
          onCancel={onClose}
          submitLabel="Create action"
          submitting={mut.running}
        />
      </DrawerForm>
    </Drawer>
  );
}

export function ProjectActionsTab({ actions = [] }) {
  const pager = useClientPagination(actions, {
    initialPageSize: 8,
    resetKey: `project-actions-${actions.length}`,
  });
  return (
    <div className="profile-tab" style={{ marginTop: 16 }}>
      <section className="card action-project-card">
        <div className="row spread action-bucket__header">
          <h3>Project action queue</h3>
          <span className="chip outline sm">{actions.length}</span>
        </div>
        {actions.length === 0 ? (
          <EmptyState
            title="No project actions"
            description="Promote a risk or create an action from a workspace to start the queue."
          />
        ) : (
          <>
            <div className="action-bucket__stack">
              {pager.items.map((action) => (
                <ActionListCard key={action.id} action={action} />
              ))}
            </div>
            <PageViewerControls
              page={pager.page}
              label="project actions"
              pageSize={pager.pageSize}
              onOffsetChange={pager.setOffset}
              onPageSizeChange={pager.setPageSize}
            />
          </>
        )}
      </section>
    </div>
  );
}
